import {Button} from 'platform/components';
import {Box, HStack, Show, Text, VStack} from 'platform/foundation';
import {useDateTimeFormatter} from 'platform/locale';
import {match, Pattern} from 'ts-pattern';

import {isNil, isNotNil} from 'ramda';

import {UserAbsenceResponseBody} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {getUserName} from '@dms/shared';

import {parseDate} from 'shared/web';

import {AvailabilityStatusDot} from './AvailabilityStatusDot';

interface AbsenceCardProps {
  absence: UserAbsenceResponseBody | null;
  isReturning: boolean;
  onReturnEarly: () => void;
  onManage: () => void;
}

export function AbsenceCard(props: AbsenceCardProps) {
  const format = useDateTimeFormatter();

  if (isNil(props.absence)) {
    return null;
  }

  const contact = props.absence.absenceContactUser;
  const deputyName = getUserName(contact) ?? contact?.email;

  const title = match(props.absence.dateTo)
    .with(Pattern.nullish, () => i18n.t('entity.userAbsence.labels.headerTitleOpenEnded'))
    .otherwise((dateTo) =>
      i18n.t('entity.userAbsence.labels.headerTitleWithReturn', {
        date: `${format('dateMedium', parseDate(dateTo))} ${format('timeShort', parseDate(dateTo))}`,
      })
    );

  return (
    <Box padding={2} borderRadius="small" backgroundColor="palettes.red.10.100">
      <HStack spacing={2} align="flex-start">
        <Box paddingTop={1}>
          <AvailabilityStatusDot status="absent" />
        </Box>
        <VStack spacing={2} width="100%">
          <VStack spacing={1}>
            <Text size="small" color="danger">
              {title}
            </Text>
            <Show when={isNotNil(deputyName)}>
              <Text size="xSmall" color="danger">
                {i18n.t('entity.userAbsence.labels.headerDeputy', {name: deputyName ?? ''})}
              </Text>
            </Show>
          </VStack>
          <HStack spacing={2}>
            <Button
              variant="secondary"
              size="small"
              title={i18n.t('entity.userAbsence.actions.manage')}
              onClick={props.onManage}
              data-testid="availabilityStatus-manage"
            />
            <Button
              variant="dangerOutlined"
              size="small"
              isLoading={props.isReturning}
              title={i18n.t('entity.userAbsence.actions.returnEarly')}
              onClick={props.onReturnEarly}
              data-testid="availabilityStatus-returnEarly"
            />
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );
}
